import { Injectable } from '@angular/core';
import { environment } from '../../../assets/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UrlReferenceService {
  private readonly baseUrl =
    environment.backend.employeeManagmentService!.baseApiUrl;
  private readonly authUrl = environment.backend.authService?.baseApiUrl;
  private readonly searchUrl = environment.backend.searchService?.baseApiUrl;
  private readonly senderUrl = environment.backend.senderService?.baseApiUrl;

  // Company URLs
  get saveCompanyUrl(): string {
    return `${this.authUrl}/companies/save`;
  }

  get updateCompanyUrl(): string {
    return `${this.authUrl}/companies/update`;
  }

  get saveCompanyDetailsUrl(): string {
    return `${this.authUrl}/companies/save-details`;
  }

  get verifyCompanyCrUrl(): string {
    return `${this.authUrl}/companies/verify-cr`;
  }

  get getCompanyByCrUrl(): string {
    return `${this.authUrl}/companies/company`;
  }

  get assignUserToCompanyUrl(): string {
    return `${this.authUrl}/companies/assign-user`;
  }

  get removeUserFromCompanyUrl(): string {
    return `${this.authUrl}/companies/remove-user`;
  }

  get getUserCompaniesUrl(): string {
    return `${this.authUrl}/companies`;
  }

  get quotationsUrl(): string {
    return `${this.searchUrl}/quotations`;
  }

  getCompanyUsersUrl(companyId: string): string {
    return `${this.authUrl}/companies/${companyId}/users`;
  }

  // Employee URLs
  get downloadSampleExcelUrl(): string {
    return `${this.baseUrl}/files/download-sample`;
  }

  get uploadEmployeesExcelUrl(): string {
    return `${this.baseUrl}/upload`;
  }

  getDeleteEmployeeUrl(id: string, sponsorId: string): string {
    return `${this.baseUrl}/${sponsorId}/employee/${id}`;
  }

  getUpdateEmployeeUrl(id: number | string, sponsorId: string): string {
    return `${this.baseUrl}/${sponsorId}/company/${id}`;
  }

  getEmployeeByIdUrl(id: string, sponsorId: string): string {
    return `${this.baseUrl}/${sponsorId}/company/${id}`;
  }

  getAllEmployeesUrl(id: string): string {
    return `${this.baseUrl}/${id}/employees`;
  }

  // New Employee Management Controller URLs
  getEmployeesByCompanyUrl(companyId: string): string {
    return `${this.baseUrl}/${companyId}/employees`;
  }

  searchEmployeesByCompanyUrl(companyId: string): string {
    return `${this.baseUrl}/${companyId}/employees/search`;
  }

  getEmployeesBySponsorUrl(sponsorId: string): string {
    return `${this.baseUrl}/${sponsorId}/employees`;
  }

  searchEmployeesUrl(): string {
    return `${this.baseUrl}/search`;
  }

  // Member URLs
  get addMemberUrl(): string {
    return `${this.baseUrl}/save`;
  }

  addMemberUrlWithId(id: string): string {
    return `${this.baseUrl}/${id}/save-memeber`;
  }

  get getMemberUrl(): string {
    return `${this.baseUrl}`;
  }

  // Authentication URLs
  getSignupUrl(): string {
    return `${this.authUrl}/signup`;
  }

  getOtpUrl(): string {
    return `${this.authUrl}/generateOtp`;
  }

  getRegenerateOtpUrl(): string {
    return `${this.authUrl}/reGenerateOtp`;
  }

  getValidateOtpUrl(): string {
    return `${this.authUrl}/validateOtp`;
  }

  // Health Declaration
  getHealthDeclarationUrl(): string {
    const companyId = localStorage.getItem('companyId') || '';
    return `${this.baseUrl}/health-declaration/${companyId}`;
  }

  CreatHealthDeclarationByMemeberUrl(Id: string): string {
    const companyId = localStorage.getItem('companyId') || '';
    return `${this.baseUrl}/health-declaration/${companyId}/${Id}type/employee`;
  }

  getHealthDeclarationByMemeberUrl(Id: string): string {
    const companyId = localStorage.getItem('companyId') || '';
    return `${this.baseUrl}/health-declaration/${companyId}/${Id}/type/employee`;
  }

  // patch api call
  patchHealthDeclarationByMemeberUrl(Id: string): string {
    const companyId = localStorage.getItem('companyId') || '';
    return `${this.baseUrl}/health-declaration/${companyId}/${Id}/type/employee`;
  }

  // Quotations URLs
  get quotationsBaseUrl(): string {
    return `${this.searchUrl}/quotations`;
  }

  getQuotationsListUrl(providerName?: string, status?: string): string {
    let url = `${this.quotationsBaseUrl}/list`;
    const params = [];

    if (providerName) params.push(`providerName=${providerName}`);
    if (status) params.push(`status=${status}`);

    return params.length > 0 ? `${url}?${params.join('&')}` : url;
  }

  getQuotationByIdUrl(id: string | number): string {
    return `${this.quotationsBaseUrl}/${id}`;
  }

  // Policies URLs
  get policiesBaseUrl(): string {
    return `${this.searchUrl}/policies`;
  }

  get activePoliciesUrl(): string {
    return `${this.policiesBaseUrl}/active`;
  }

  get renewedPoliciesUrl(): string {
    return `${this.policiesBaseUrl}/renewed`;
  }

  get aboutToExpirePoliciesUrl(): string {
    return `${this.policiesBaseUrl}/about-to-expire`;
  }

  get pendingPoliciesUrl(): string {
    return `${this.policiesBaseUrl}/pending`;
  }

  get expiredPoliciesUrl(): string {
    return `${this.policiesBaseUrl}/expired`;
  }

  get cancelledPoliciesUrl(): string {
    return `${this.policiesBaseUrl}/cancelled`;
  }

  get searchPoliciesUrl(): string {
    return `${this.policiesBaseUrl}/search`;
  }

  getPolicyByIdUrl(id: string | number): string {
    return `${this.policiesBaseUrl}/${id}`;
  }

  // Payer URLs (from sender collection)
  get payersBaseUrl(): string {
    return `${this.senderUrl}/payers`;
  }

  sendQuotasUrl(payerId: string | number): string {
    return `${this.payersBaseUrl}/send-quotas/${payerId}`;
  }

  sendVerifyToPayerUrl(): string {
    return `${this.payersBaseUrl}/send-verify`;
  }

  createPolicyUrl(): string {
    return `${this.payersBaseUrl}/issue-policy`;
  }

  listQuotasUrl(payerId: string | number): string {
    return `${this.payersBaseUrl}/list-quotas/${payerId}`;
  }

  // Base URLs
  get currentBaseUrl(): string {
    return this.baseUrl;
  }

  get currentAuthUrl(): string {
    return this.authUrl;
  }

  get currentSearchUrl(): string {
    return this.searchUrl ?? '';
  }

  get currentSenderUrl(): string {
    return this.senderUrl ?? '';
  }

  // Dashboard Metrics URLs
  getDashboardMetricsTotalEmployeesUrl(userId: string): string {
    return `${this.searchUrl}/dashboard/metrics/total-employees/${userId}`;
  }

  getDashboardMetricsTotalCompaniesUrl(userId: string): string {
    return `${this.searchUrl}/dashboard/metrics/total-companies/${userId}`;
  }

  getDashboardMetricsTotalActivePoliciesUrl(userId: string): string {
    return `${this.searchUrl}/dashboard/metrics/total-active-policies/${userId}`;
  }

  getDashboardMetricsActiveQuotesUrl(userId: string): string {
    return `${this.searchUrl}/dashboard/metrics/total-active-quotas/${userId}`;
  }

  //setting update profile and change password URLs
  getUpdateProfileUrl(): string {
    return `${this.authUrl}/users/profile`
  }

  getChangePasswordUrl(): string {
    return `${this.authUrl}/users/change-password`
  }

  getVerifyCurrentPasswordUrl(): string {
    return `${this.authUrl}/users/verify-password`
  }
}
